import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PermissionKey, TenantPrincipalDto } from '@cuito/types';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: TenantPrincipalDto | null;
  /** 仅刷新令牌时使用：更新令牌但保留 user */
  setTokens: (accessToken: string, refreshToken: string) => void;
  /** 登录成功：一次性写入令牌与主体信息 */
  setSession: (payload: {
    accessToken: string;
    refreshToken: string;
    user: TenantPrincipalDto;
  }) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasPermission: (permission: PermissionKey) => boolean;
}

/**
 * 社团端鉴权状态（localStorage 持久化）。
 *
 * `hasPermission` 的两条语义：
 *   - 未登录 → false
 *   - `OWNER` → **恒为 true**（与后端 `resolvePermissions` 一致：主账号权限不可被剥夺）
 *   - 其他角色 → 查令牌里已展开的 `permissions` 数组
 *
 * 前端据此渲染菜单；真正的拦截仍在后端（`PermissionsGuard`），前端隐藏只是体验。
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      setSession: ({ accessToken, refreshToken, user }) =>
        set({ accessToken, refreshToken, user }),

      logout: () => set({ accessToken: null, refreshToken: null, user: null }),

      isAuthenticated: () => Boolean(get().accessToken && get().user),

      hasPermission: (permission) => {
        const user = get().user;
        if (!user) return false;
        if (user.role === 'OWNER') return true;
        return user.permissions.includes(permission);
      },
    }),
    {
      name: 'cuito-tenant-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);
